import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { UpsertEntryBody, newsletterApi } from '@/services/api/newsletter/newsletter';

export const newsletterKeys = {
  issues: ['newsletter', 'issues'] as const,
  openIssue: ['newsletter', 'open-issue'] as const,
  myMissingCount: ['newsletter', 'my-missing-count'] as const,
  mySlots: (issueId: string) => ['newsletter', 'my-slots', issueId] as const,
  entries: (issueId: string) => ['newsletter', 'entries', issueId] as const,
  outstanding: (issueId: string) => ['newsletter', 'outstanding', issueId] as const,
  sendPreview: (issueId: string) => ['newsletter', 'send-preview', issueId] as const,
  stats: (issueId: string) => ['newsletter', 'stats', issueId] as const,
};

export function useIssuesQuery() {
  return useQuery({ queryKey: newsletterKeys.issues, queryFn: () => newsletterApi.getIssues() });
}

export function useOpenIssueQuery() {
  return useQuery({ queryKey: newsletterKeys.openIssue, queryFn: () => newsletterApi.getOpenIssue() });
}

/** Slots the current user still has to submit for the open issue — feeds the sidebar badge. */
export function useMyMissingNewsletterCountQuery(enabled = true) {
  return useQuery({
    queryKey: newsletterKeys.myMissingCount,
    queryFn: () => newsletterApi.getMyMissingCount(),
    enabled,
  });
}

export function useMySlotsQuery(issueId: string | undefined) {
  return useQuery({
    queryKey: newsletterKeys.mySlots(issueId ?? ''),
    queryFn: () => newsletterApi.getMySlots(issueId as string),
    enabled: Boolean(issueId),
  });
}

export function useIssueEntriesQuery(issueId: string | undefined) {
  return useQuery({
    queryKey: newsletterKeys.entries(issueId ?? ''),
    queryFn: () => newsletterApi.getEntries(issueId as string),
    enabled: Boolean(issueId),
  });
}

export function useOutstandingQuery(issueId: string | undefined) {
  return useQuery({
    queryKey: newsletterKeys.outstanding(issueId ?? ''),
    queryFn: () => newsletterApi.getOutstanding(issueId as string),
    enabled: Boolean(issueId),
  });
}

/** Refetched fresh on every open (not just once per staleTime window) — the confirm modal for a
 *  once-ever send must never show a stale "not sent yet" a few seconds after another tab sent it. */
export function useSendPreviewQuery(issueId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: newsletterKeys.sendPreview(issueId ?? ''),
    queryFn: () => newsletterApi.getSendPreview(issueId as string),
    enabled: Boolean(issueId) && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/** Readership only accrues after publish, and the panel that renders this is only shown for a
 *  published issue — no `enabled` gate needed here beyond the usual `Boolean(issueId)`. */
export function useIssueStatsQuery(issueId: string | undefined) {
  return useQuery({
    queryKey: newsletterKeys.stats(issueId ?? ''),
    queryFn: () => newsletterApi.getStats(issueId as string),
    enabled: Boolean(issueId),
  });
}

function invalidateIssue(queryClient: ReturnType<typeof useQueryClient>, issueId: string) {
  queryClient.invalidateQueries({ queryKey: newsletterKeys.mySlots(issueId) });
  queryClient.invalidateQueries({ queryKey: newsletterKeys.entries(issueId) });
  queryClient.invalidateQueries({ queryKey: newsletterKeys.outstanding(issueId) });
  queryClient.invalidateQueries({ queryKey: newsletterKeys.myMissingCount });
}

export function useCreateEntryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ issueId, body }: { issueId: string; body: UpsertEntryBody }) =>
      newsletterApi.createEntry(issueId, body),
    onSuccess: (_, variables) => invalidateIssue(queryClient, variables.issueId),
  });
}

export function useUpdateEntryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ issueId, entryId, body }: { issueId: string; entryId: string; body: UpsertEntryBody }) =>
      newsletterApi.updateEntry(issueId, entryId, body),
    onSuccess: (_, variables) => invalidateIssue(queryClient, variables.issueId),
  });
}

export function useSubmitEntryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { issueId: string; entryId: string }) => newsletterApi.submitEntry(variables.entryId),
    onSuccess: (_, variables) => invalidateIssue(queryClient, variables.issueId),
  });
}

export function useDeleteEntryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { issueId: string; entryId: string }) => newsletterApi.deleteEntry(variables.entryId),
    onSuccess: (_, variables) => invalidateIssue(queryClient, variables.issueId),
  });
}

export function useDismissSlotMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      issueId,
      body,
    }: {
      issueId: string;
      body: { gradeLevel: number; subjectId?: string; subjectLabel: string };
    }) => newsletterApi.dismissSlot(issueId, body),
    onSuccess: (_, variables) => invalidateIssue(queryClient, variables.issueId),
  });
}

export function useUndismissSlotMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { issueId: string; dismissalId: string }) =>
      newsletterApi.undismissSlot(variables.dismissalId),
    onSuccess: (_, variables) => invalidateIssue(queryClient, variables.issueId),
  });
}

export function useReorderFrontMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { issueId: string; entryIds: string[] }) =>
      newsletterApi.reorderFront(variables.issueId, variables.entryIds),
    onSuccess: (_, variables) => queryClient.invalidateQueries({ queryKey: newsletterKeys.entries(variables.issueId) }),
  });
}

export function useCreateIssueMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof newsletterApi.createIssue>[0]) => newsletterApi.createIssue(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.issues });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.openIssue });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.myMissingCount });
    },
  });
}

export function useUpdateIssueMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { issueId: string } & Parameters<typeof newsletterApi.updateIssue>[1]) => {
      const { issueId, ...body } = variables;
      return newsletterApi.updateIssue(issueId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.issues });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.openIssue });
    },
  });
}

export function usePublishMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (issueId: string) => newsletterApi.publish(issueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.issues });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.openIssue });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.myMissingCount });
    },
  });
}

export function useUnpublishMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (issueId: string) => newsletterApi.unpublish(issueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: newsletterKeys.issues });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.openIssue });
      queryClient.invalidateQueries({ queryKey: newsletterKeys.myMissingCount });
    },
  });
}

/** The send mutation's response IS the fresh truth (the DB write already happened by the time it
 *  resolves), so it seeds the send-preview cache directly instead of waiting on a refetch — the
 *  confirm button's disabled state must flip the instant this resolves, not after another round trip. */
export function useSendMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (issueId: string) => newsletterApi.send(issueId),
    onSuccess: (data, issueId) => {
      queryClient.setQueryData(newsletterKeys.sendPreview(issueId), data);
      queryClient.invalidateQueries({ queryKey: newsletterKeys.issues });
    },
  });
}
